import React, { useMemo, useState } from 'react'
import { PanelHeader } from 'Components/common/TaskPanelHeader/TaskPanelHeader'
import ExpertThoughtsIcon from 'Assets/icons/expert-thoughts.svg'
import styles from './ExpertThoughts.module.less'
import Divider from 'antd/lib/divider'
import Collapse from 'antd/lib/collapse'
import { Banner } from 'Components/common/Banner/Banner'
import { AttributeValueType } from 'Types/Attribute.types'
import { TaskAttribute } from 'Types/Tasks.types'
import { AttributesFactory } from 'Core/AttributesFactory'
import Flex from 'antd/lib/flex'
import { isEmpty } from 'lodash'
import Popover from 'antd/lib/popover'
import { ExpertThoughtsIcon as ExpertThoughtsIconComponent } from 'Components/common/Icons/ExpertThoughts'
import { CloseOutlined } from '@ant-design/icons'
import Tooltip from 'antd/lib/tooltip'
import InfoCircleFilled from '@ant-design/icons/lib/icons/InfoCircleFilled'

export type ExpertThoughtsProps = {
    title: 'EXPERT’S THOUGHTS' | 'REWORK’S FEEDBACK'
    items: {
        message?: string
        rejectionReasons?: string[]
        attributesList:
            | {
                  label: string
                  value: AttributeValueType
                  config: TaskAttribute
              }[]
            | null
        panelHeader: React.ReactElement | string
    }[]
    bannerInfo: string
    defaultActivePanel: 'first' | 'last'
    highlightThoughts: boolean
    onPopoverClose?: () => void
}

type NonNullableAttributeListType = NonNullable<
    ExpertThoughtsProps['items'][number]['attributesList']
>

const ExpertThoughts: React.FC<ExpertThoughtsProps> = ({
    title,
    items,
    bannerInfo,
    defaultActivePanel,
    highlightThoughts,
    onPopoverClose,
}) => {
    const [popoverVisible, setPopoverVisible] = useState(highlightThoughts)

    const renderAttribute = (attribute: NonNullableAttributeListType[number]) => (
        <div key={attribute.config.version_id}>
            <Flex gap={5}>
                <div className={styles['attribute-label']}>{attribute.label}</div>
                {attribute.config.description && (
                    <Tooltip title={attribute.config.description}>
                        <InfoCircleFilled className={styles['info-icon']} />
                    </Tooltip>
                )}
            </Flex>
            <AttributesFactory
                readonly
                onChange={() => {}}
                className={`${styles['attribute']} ${
                    styles[attribute.config.input_definition?.input_type.toLowerCase() || '']
                }`}
                {...attribute}
            />
        </div>
    )

    const renderAttributesList = useMemo(
        () => (attributesList: NonNullableAttributeListType) => attributesList.map(renderAttribute),
        []
    )

    const collapseItems = useMemo(
        () =>
            items
                .map((item, index) => {
                    if (
                        !item.message &&
                        isEmpty(item.attributesList) &&
                        isEmpty(item.rejectionReasons)
                    )
                        return null
                    return (
                        <Collapse.Panel
                            className={styles['collapse-panel']}
                            header={item.panelHeader}
                            key={`item${index}`}
                        >
                            <Flex vertical gap={16} className={styles['panel-content']}>
                                {item.attributesList && renderAttributesList(item.attributesList)}
                                {!!item.rejectionReasons?.length && (
                                    <div className={styles['expert-main']}>
                                        <div className={styles['title']}>Rejection Reasons</div>
                                        <div className={styles['message']}>
                                            {item.rejectionReasons.join(', ')}
                                        </div>
                                    </div>
                                )}
                                {item.message && (
                                    <div className={styles['expert-main']}>
                                        <div className={styles['title']}>Comments</div>
                                        <div className={styles['message']}>{item.message}</div>
                                    </div>
                                )}
                            </Flex>
                        </Collapse.Panel>
                    )
                })
                .filter(Boolean),
        [items, renderAttributesList]
    )

    const popoverTitle = useMemo(
        () => (
            <Flex gap={6} align="flex-start" className={styles['popover']}>
                <div className={styles['popover-icon']}>
                    <ExpertThoughtsIconComponent color="#FFAB00" />
                </div>
                <Flex vertical gap={4}>
                    <div className={styles['popover-heading']}>You have a new comment</div>
                    <div className={styles['popover-detail']}>
                        Please check all the comments & make necessary changes to the output.
                    </div>
                </Flex>
                <CloseOutlined
                    className={styles['popover-close-icon']}
                    onClick={() => {
                        setPopoverVisible(false)
                        onPopoverClose?.()
                    }}
                />
            </Flex>
        ),
        [onPopoverClose]
    )

    const thoughtsContent = useMemo(
        () => (
            <div
                className={styles['expert-parent'] + ' ' + (popoverVisible ? styles['active'] : '')}
                data-testid="experts-thoughts-container"
            >
                <PanelHeader title={title} logo={ExpertThoughtsIcon} />
                <div className={styles['expert-body']}>
                    <Banner message={bannerInfo} />
                    <Divider orientation="center" className={styles['divider']}>
                        Comments
                    </Divider>
                    <Collapse
                        accordion
                        expandIconPosition={'end'}
                        defaultActiveKey={
                            defaultActivePanel === 'first' ? 'item0' : `item${items.length - 1}`
                        }
                        ghost
                    >
                        {collapseItems}
                    </Collapse>
                </div>
            </div>
        ),
        [bannerInfo, collapseItems, defaultActivePanel, items.length, popoverVisible, title]
    )

    return (
        <div className={styles['container']}>
            {popoverVisible ? (
                <>
                    <div className={styles['overlay']} />
                    <Popover
                        title={popoverTitle}
                        open
                        placement="rightTop"
                        rootClassName={styles['popover-container']}
                        destroyTooltipOnHide
                    >
                        {thoughtsContent}
                    </Popover>
                </>
            ) : (
                thoughtsContent
            )}
        </div>
    )
}

export default ExpertThoughts
